import {
  CsatAccessChannel,
  CsatDeviceType,
  PendingCsatSurveyTrigger,
} from '../models/Csat';

const APP_SESSION_STORAGE_KEY = 'csat-app-session-id';
const PENDING_CSAT_TRIGGER_STORAGE_KEY = 'pending-csat-trigger';

export const ADD_RECIPE_CSAT_TRIGGER_CONTEXT = 'add_recipe';
export const RECIPE_CREATION_CSAT_TRIGGER_CONTEXT = ADD_RECIPE_CSAT_TRIGGER_CONTEXT;
export const ADD_EQUIPMENT_CSAT_TRIGGER_CONTEXT = 'add_equipment';
export const ADD_USER_CSAT_TRIGGER_CONTEXT = 'add_user';
export const ADD_MENU_CSAT_TRIGGER_CONTEXT = 'add_menu';
export const SEND_MENU_CSAT_TRIGGER_CONTEXT = 'send_menu';
export const SEND_SOFTWARE_CSAT_TRIGGER_CONTEXT = 'send_software';
export const SAVE_COOKBOOK_RECIPE_CSAT_TRIGGER_CONTEXT = 'save_cookbook_recipe';
export const DELETE_RECIPE_CSAT_TRIGGER_CONTEXT = 'delete_recipe';
export const REORDER_RECIPE_CSAT_TRIGGER_CONTEXT = 'reorder_recipe';
export const PENDING_CSAT_TRIGGER_EVENT = 'csat:pending-trigger';

const createFallbackSessionId = () => {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const getCsatAccessChannel = (): CsatAccessChannel => {
  const isPwa =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

  if (isPwa) {
    return 'pwa';
  }

  const userAgent = window.navigator.userAgent;
  const isMobileBrowser = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent
  );

  return isMobileBrowser ? 'mobile_browser' : 'web';
};

export const getCsatDeviceType = (): CsatDeviceType => {
  const width = window.innerWidth;

  if (width <= 768) {
    return 'mobile';
  }

  if (width <= 1024) {
    return 'tablet';
  }

  return 'desktop';
};

export const getCsatAppSessionId = (): string => {
  const storedSessionId = window.sessionStorage.getItem(APP_SESSION_STORAGE_KEY);

  if (storedSessionId) {
    return storedSessionId;
  }

  const newSessionId = window.crypto?.randomUUID?.() ?? createFallbackSessionId();
  window.sessionStorage.setItem(APP_SESSION_STORAGE_KEY, newSessionId);

  return newSessionId;
};

export const getCsatClaritySessionId = (): string | null => {
  const clarityCookie = document.cookie
    .split('; ')
    .find(cookie => cookie.startsWith('_clsk=') || cookie.startsWith('_clck='));

  if (!clarityCookie) {
    return null;
  }

  const [, rawValue = ''] = clarityCookie.split('=');
  const [sessionId] = rawValue.split('|');

  return sessionId || null;
};

export const buildCsatTrackingData = () => ({
  accessChannel: getCsatAccessChannel(),
  deviceType: getCsatDeviceType(),
  pageUrl: `${window.location.pathname}${window.location.search}`,
  appSessionId: getCsatAppSessionId(),
  claritySessionId: getCsatClaritySessionId(),
});

export const readPendingCsatSurveyTrigger = (): PendingCsatSurveyTrigger | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawTrigger = window.sessionStorage.getItem(PENDING_CSAT_TRIGGER_STORAGE_KEY);

  if (!rawTrigger) {
    return null;
  }

  try {
    return JSON.parse(rawTrigger) as PendingCsatSurveyTrigger;
  } catch (error) {
    window.sessionStorage.removeItem(PENDING_CSAT_TRIGGER_STORAGE_KEY);
    return null;
  }
};

export const queuePendingCsatSurveyTrigger = (trigger: PendingCsatSurveyTrigger) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (readPendingCsatSurveyTrigger()) {
    return;
  }

  window.sessionStorage.setItem(PENDING_CSAT_TRIGGER_STORAGE_KEY, JSON.stringify(trigger));
  window.dispatchEvent(new Event(PENDING_CSAT_TRIGGER_EVENT));
};

export const consumePendingCsatSurveyTrigger = (): PendingCsatSurveyTrigger | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const trigger = readPendingCsatSurveyTrigger();

  if (trigger) {
    window.sessionStorage.removeItem(PENDING_CSAT_TRIGGER_STORAGE_KEY);
  }

  return trigger;
};
